// © 2026 Holmes Computer Consultants. All rights reserved. -- www.holmesconsultants.com
//
// The client (M3-37, 08 §2).
//
// `createBrowserRouter` and React Router 8, imported from **`react-router`** — never
// `react-router-dom`, which stops at 7.18.1 and has no v8 at all.
//
// **The route table is data and lives in `routes.ts`**, where it can be tested and where the
// navigation bar reads it to find out which destinations exist yet. What is here is the mapping
// from those paths to components, and one assertion that the two have not drifted: a route the
// table says is served and this file has no component for is a link the navigation renders as
// working and the router answers with a 404.
//
// Most routes are **not** lazily split. The whole client is one machine and one shell — the
// machine definition, the geometry, the art program and the audio synthesis are the bundle, and
// they are needed on the front door. Splitting the small route components out of that would move
// about a kilobyte and add a network round trip to every tab of the navigation bar.
//
// **The account routes are the exception, and for a reason that is not about size.** Everything
// under `./account` pulls in the Supabase SDK and the code that talks to a server, and FR-030 says
// an anonymous visitor plays a whole game without any of that happening. Lazily importing them is
// what makes that a fact about the bundle rather than a promise about the code:
// `scripts/check-anonymous.mjs` stops its walk at that module and refuses any `fetch` reachable
// from the front door without it.

import { StrictMode, Suspense, lazy } from 'react'
import { createRoot } from 'react-dom/client'
import { ErrorBoundary } from './components/ErrorBoundary'
import { RouterProvider, createBrowserRouter } from 'react-router'
import type { RouteObject } from 'react-router'
import { AppShell } from './components/AppShell'
import { Landing } from './routes/Landing'
import { Arcade } from './routes/Arcade'
import { PlayRoute } from './routes/PlayRoute'
import { Pricing } from './routes/Pricing'
import { Headset } from './routes/Headset'
import { installEmulatedXR } from '../xr/emulation'
import { Settings } from './routes/Settings'
import { NotFound } from './routes/NotFound'
import { CopyPage } from './components/CopyPage'
import { ThemeController } from './theme'
import { initialiseLanguage, useLocalisation } from './i18n/useLanguage'
import { isLazyRoute } from './routes'
import { servedRoutes } from './routes'
import './shell.css'

/**
 * The lazily-loaded-page placeholder, as a component rather than inline markup.
 *
 * It has to be one: it says a word, the word is translated, and `useLocalisation` is a hook — so it
 * cannot be called from `children()`, which is a plain function that runs once at start-up. A
 * component defers the lookup to render time, which is also when the language is known.
 */
function SuspenseFallback(): React.JSX.Element {
  const { t } = useLocalisation()
  return (
    <p className="page muted" role="status">
      {t('common.loading')}
    </p>
  )
}

const theme = new ThemeController()

// **`<html lang>` before the first paint, beside the theme and for the same reason.** A screen
// reader picks its voice from that attribute, so a French page that renders as `lang="en"` is read
// aloud in an English accent — which is a worse outcome than not having translated it. Done here
// rather than at module load so that importing this file in a test does not require a document.
initialiseLanguage()

/**
 * The account subtree, behind one dynamic import.
 *
 * One import for all four, so the boundary the anonymous-funnel check records is one module. A
 * `lazy` call per route would be four chunks, four entries in that allowlist, and four places to
 * add a fifth without anybody noticing.
 */
const account = () => import('./account')
const AccountPage = lazy(async () => ({ default: (await account()).AccountRoute }))
const BoardsPage = lazy(async () => ({ default: (await account()).BoardsRoute }))
const FriendsPage = lazy(async () => ({ default: (await account()).FriendsRoute }))
const HistoryPage = lazy(async () => ({ default: (await account()).HistoryRoute }))
const ProfilePage = lazy(async () => ({ default: (await account()).ProfileRoute }))
const TournamentsPage = lazy(async () => ({ default: (await account()).TournamentsRoute }))
const GhostPage = lazy(async () => ({ default: (await account()).GhostRoute }))
const LinkHeadsetPage = lazy(async () => ({ default: (await account()).LinkHeadsetRoute }))
const ModerationPage = lazy(async () => ({ default: (await account()).ModerationRoute }))

/**
 * The Machine Builder, behind its own dynamic import (M9-01).
 *
 * A separate boundary from the account subtree rather than a fifth entry in it: the two are lazy for
 * different reasons — that one is FR-030's, this one is weight — and a visitor who opens their
 * account has no use for a layout canvas.
 */
const BuilderPage = lazy(async () => ({ default: (await import('../builder')).BuilderRoute }))
/** The same chunk, opening a machine the URL names (G1-11). */
const BuilderMachinePage = lazy(async () => ({
  default: (await import('../builder')).OpenMachineRoute,
}))
/**
 * A machine's guide (S3-10), behind its own import because the module it reads is every guide's
 * words at once, and the front door should not pay for them.
 */
const MachineGuidePage = lazy(async () => ({
  default: (await import('./routes/MachineGuide')).MachineGuideRoute,
}))

/**
 * Path → component, for every route `routes.ts` says is served today.
 *
 * `ComponentType` rather than `() => Element`, because a lazily loaded page is a
 * `LazyExoticComponent` and not a plain function. Widening the map is the honest fix; casting the
 * lazy ones would have been a cast that says "trust me" about the one difference that matters
 * here.
 */
const PAGES: Readonly<Record<string, React.ComponentType>> = {
  '/': Landing,
  '/arcade': Arcade,
  '/pricing': Pricing,
  '/play/:machineId': PlayRoute,
  '/machines/:machineId': MachineGuidePage,
  '/settings': () => <Settings theme={theme} />,
  '/headset': Headset,
  '/account': AccountPage,
  '/leaderboards': BoardsPage,
  '/leaderboards/:machineId': BoardsPage,
  '/friends': FriendsPage,
  '/history': HistoryPage,
  '/profile/:handle': ProfilePage,
  '/tournaments': TournamentsPage,
  '/replays/:replayId': GhostPage,
  '/link-headset': LinkHeadsetPage,
  '/moderation': ModerationPage,
  '/builder': () => <BuilderPage theme={theme} />,
  '/builder/:machineId': () => <BuilderMachinePage theme={theme} />,
  // The six documents (S4). One component, six paths: the words are in `src/seo/pageCopy.ts`,
  // which the prerenderer also reads, so what a crawler is served and what a visitor is shown are
  // the same text rather than two copies of it.
  //
  // Eager rather than lazy, unlike the account and Builder chunks. These are strings — they add no
  // dependency, reach no network and construct nothing, so a chunk boundary here would buy a round
  // trip and cost the front door nothing it gets back. `check-payload` reports what they cost.
  '/how-to-play': () => <CopyPage path="/how-to-play" />,
  '/privacy': () => <CopyPage path="/privacy" />,
  '/accessibility': () => <CopyPage path="/accessibility" />,
  '/terms': () => <CopyPage path="/terms" />,
  '/developers': () => <CopyPage path="/developers" />,
  '/connect': () => <CopyPage path="/connect" />,
  '/support': () => <CopyPage path="/support" />,
  '/about': () => <CopyPage path="/about" />,
}

/**
 * Build the children, and refuse to start if the table and this file disagree.
 *
 * A served route with no component here would render as a working navigation link that answers
 * with "nothing at this address" — the failure would be a 404 on a tab, which reads as a broken
 * product rather than as a missing line in a map. Cheap to check, and it is checked at start-up
 * rather than in a test because the table is the thing a future route is added to.
 */
function children(): RouteObject[] {
  const routes: RouteObject[] = []
  for (const spec of servedRoutes()) {
    const Page = PAGES[spec.path]
    if (Page === undefined) throw new Error(`routes.ts serves ${spec.path} and main.tsx has no page for it`)
    // A lazily loaded page needs something on screen while its chunk arrives, and it needs it to
    // be a *live region* — a player on a slow connection who taps Boards and sees an unchanged
    // page has been given no feedback at all, and a screen-reader player has been given less.
    const Mounted = isLazyRoute(spec.path)
      ? () => (
          <Suspense fallback={<SuspenseFallback />}>
            <Page />
          </Suspense>
        )
      : Page
    routes.push(
      spec.path === '/' ? { index: true, Component: Mounted } : { path: spec.path, Component: Mounted },
    )
  }
  routes.push({ path: '*', Component: NotFound })
  return routes
}

const router = createBrowserRouter([
  {
    path: '/',
    Component: () => <AppShell theme={theme} />,
    children: children(),
  },
])

const host = document.getElementById('root')
if (host === null) throw new Error('index.html has no #root')

// The emulated headset (M5-30), before anything reads `navigator.xr` — IWER works by replacing
// the global, so it has to be in place first, and the machine route asks whether VR is supported
// as soon as it mounts. `import.meta.env.DEV` inside `installEmulatedXR` makes the whole call
// statically unreachable in production, so neither the emulator nor the query parameter that
// installs it exists in a shipped bundle.
await installEmulatedXR(globalThis.location.search)

// The skeleton cabinet that `index.html` painted before any of this parsed is inside `#root`, so
// mounting replaces it. `createRoot` rather than `hydrateRoot`: the skeleton is deliberately not
// the same tree React is about to render, and asking React to reconcile with it would produce a
// hydration mismatch warning for markup that was never meant to survive.
// **Outside `RouterProvider`, and that is the whole point** (M3-43). React unmounts the entire
// tree when a render throws, so a boundary inside a route catches only what that route did — and
// the failures worth catching are the ones nobody predicted, including one in the router itself.
// A boundary here is the difference between a message and a blank body.
createRoot(host).render(
  <StrictMode>
    <ErrorBoundary>
      <RouterProvider router={router} />
    </ErrorBoundary>
  </StrictMode>,
)

/*
  Offline play (M3-47).

  **This product can be completely offline and was not, for one missing file.** The simulation is
  pure and dependency-free, every asset is generated at run time, the front-door payload is under
  400 KB, and anonymous play reaches no backend at all (FR-030). A player on a plane had everything
  they needed except the ability to load the JavaScript.

  **After the render, and after `load`.** Registering earlier competes with the first paint for
  the network on the one load where a stranger is deciding whether to stay — the worker's whole job
  is to help the *second* visit, so it can wait for the first one to finish.

  **Not registered in development.** A worker that has cached a dev-server bundle survives a
  restart and serves it against a new one, which presents as a page that is mysteriously two edits
  behind. `import.meta.env.DEV` is statically false in a production build, so this whole block is
  dropped from the shipped bundle rather than being a run-time branch.

  Failure is silence: a browser with no service workers, a private window that refuses one, or an
  origin without HTTPS. All of them are a player who gets exactly the product they get today.
*/
if (!import.meta.env.DEV && 'serviceWorker' in navigator) {
  globalThis.addEventListener('load', () => {
    void navigator.serviceWorker.register('/sw.js').catch(() => undefined)
  })
}
